/**
 * Redesigns the Pumper Bundles "Mengenrabatte" quantity-discount widget on the PDP.
 * Green "+" checklist. The product-wide "mit gratis <gift>" promise is always
 * included with the purchase regardless of quantity, so it's shown on every tier
 * (including Kaufe 1) — not gated behind a spend threshold like shipping is.
 *
 * Everything here is computed live from the prices/quantities the app itself already
 * renders (via stable ids/attributes: pumper_totalAmount_N, pumper_originalAmount_N,
 * data-tier-subtitle, data-tier-title) — it never touches the app's own pricing,
 * discount, or add-to-cart logic, only adds/reflows presentation around it.
 */
(function () {
  'use strict';

  // Keep in sync with the store's actual active discounts:
  // "Gratisversand innerhalb DE ab 50€" (DiscountAutomaticNode/1693992649030).
  var FREE_SHIPPING_THRESHOLD = 50;

  // Real DE standard shipping rate from the "General profile" delivery profile
  // (DeliveryProfile/114666668358) — confirm with merchant if the carrier rate changes.
  var SHIPPING_VALUE = 3.9;

  // No product/checkout code tracks the "gratis <gift>" promise mentioned in each
  // product title (it's baked into the product photo only) — values are maintained
  // here by hand. Add an entry per known gift name (lowercase, as it appears after
  // "mit gratis " in the product title) to surface it in the checklist.
  var GIFT_VALUE_BY_NAME = {
    'propolis-seife': { value: 16.9, url: 'https://niturenation.com/products/propolis-seife' },
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // [GRATIS-GESCHENK-2] TEMPORÄR DEAKTIVIERT — 2026-08-03
  // Grund: Curcuma 90 Kapseln ist ausverkauft, das "ab 55 €"-Geschenk kann nicht
  // mehr eingelöst werden. Zum Reaktivieren einfach alle vier mit
  // [GRATIS-GESCHENK-2] markierten Blöcke in dieser Datei wieder einkommentieren.
  //
  // Second, spend-gated gift: stacks on top of the product-wide gift above once a
  // tier's own total crosses this threshold (merchant-defined, not tied to any
  // discount/shipping automation — confirm with merchant if the threshold changes).
  // var SECOND_GIFT_THRESHOLD = 55;
  // var SECOND_GIFT = {
  //   name: 'Curcuma 90 Kapseln',
  //   value: 20.9,
  //   url: 'https://niturenation.com/products/curcuma',
  // };
  // ─────────────────────────────────────────────────────────────────────────────

  var INJECTED_CLASS = 'ntn-qb-enhance';
  var BEST_BADGE_CLASS = 'ntn-qb-best-badge';

  function parseMoney(text) {
    if (!text) return null;
    var cleaned = String(text).replace(/[^0-9.,]/g, '').trim();
    if (!cleaned) return null;
    if (cleaned.indexOf(',') !== -1) {
      cleaned = cleaned.replace(/\./g, '').replace(',', '.');
    }
    var val = parseFloat(cleaned);
    return isNaN(val) ? null : val;
  }

  function formatMoney(n) {
    return n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
  }

  function detectGift() {
    var h1 = document.querySelector('h1');
    if (!h1) return null;
    var m = h1.textContent.match(/mit gratis (.+)$/i);
    if (!m) return null;
    var name = m[1].trim().replace(/[.\s]+$/, '');
    var lowerName = name.toLowerCase();
    var key = Object.keys(GIFT_VALUE_BY_NAME).filter(function (k) {
      return lowerName.indexOf(k) !== -1;
    })[0];
    var info = key ? GIFT_VALUE_BY_NAME[key] : null;
    return { name: name, value: info ? info.value : null, url: info ? info.url : null };
  }

  // Underlined product name, linking out to the gift's own product page in a new
  // tab (no icon, no tooltip — just the plain underlined name). <a> is
  // "interactive content", so a click on it is excluded from the label's native
  // click-forwarding — it opens the link without also selecting that tier.
  function giftLink(url, name) {
    // A literal "-" is a soft-wrap opportunity by default (e.g. "Propolis-Seife"
    // could split mid-word on narrow screens) — swap in a non-breaking hyphen.
    var safeName = name.replace(/-/g, '‑');
    return (
      '<a class="ntn-qb-gift-link" href="' + url + '" target="_blank" rel="noopener" ' +
        'aria-label="Produkt ansehen: ' + safeName + '">' + safeName + '</a>'
    );
  }

  // One row per checklist item: "+ <label>" on the left, "(Wert: ...)" pinned to
  // the far right — same line if it fits, wrapping to its own line (still
  // right-aligned) only when the label itself is too long.
  function checklistRow(labelHTML, value, extraClass) {
    return (
      '<div class="ntn-qb-row' + (extraClass ? ' ' + extraClass : '') + '">' +
        '<span class="ntn-qb-plus">+</span>' +
        '<span class="ntn-qb-row-label">' + labelHTML + '</span>' +
        '<span class="ntn-qb-value">(Wert: ' + formatMoney(value) + ')</span>' +
      '</div>'
    );
  }

  function enhanceTier(i, gift) {
    var finalEl = document.getElementById('pumper_totalAmount_' + i);
    var compareEl = document.getElementById('pumper_originalAmount_' + i);
    var titleEl = document.querySelector('[data-tier-title="' + i + '"]');
    if (!finalEl || !compareEl || !titleEl) return null;

    var label = finalEl.closest('label');
    if (!label) return null;

    var finalPrice = parseMoney(finalEl.textContent);
    var comparePrice = parseMoney(compareEl.textContent);
    if (!finalPrice || !comparePrice || comparePrice <= finalPrice) return null;

    var qtyMatch = titleEl.textContent.match(/(\d+)/);
    var qty = qtyMatch ? parseInt(qtyMatch[1], 10) : 1;

    // Only tiers with the app's own "Spare X%" badge (Kaufe 2/3) are a quantity-break
    // deal worth calling out with a full checklist; Kaufe 1 is just the regular price.
    var badgeEl = document.querySelector('[data-tier-subtitle="' + i + '"]');
    var hasDiscountBadge = !!badgeEl;
    if (badgeEl && badgeEl.style.display !== 'none') badgeEl.style.display = 'none';

    var correctPercent = Math.round((1 - finalPrice / comparePrice) * 100);
    var tierTotal = finalPrice * qty;
    var savings = (comparePrice - finalPrice) * qty;
    var freeShip = hasDiscountBadge && tierTotal > FREE_SHIPPING_THRESHOLD;
    var hasGift = !!(gift && gift.value);
    // [GRATIS-GESCHENK-2] temporär deaktiviert (Curcuma ausverkauft):
    // var hasSecondGift = tierTotal > SECOND_GIFT_THRESHOLD;
    var totalValue =
      savings +
      (freeShip ? SHIPPING_VALUE : 0) +
      (hasGift ? gift.value : 0);
      // [GRATIS-GESCHENK-2] + (hasSecondGift ? SECOND_GIFT.value : 0);

    var rows = [];
    if (hasDiscountBadge) {
      rows.push(checklistRow(correctPercent + ' % SPAREN', savings));
      if (freeShip) {
        rows.push(checklistRow('Kostenloser Versand', SHIPPING_VALUE));
      }
    }
    if (hasGift) {
      rows.push(checklistRow('Gratis Geschenk ' + (gift.url ? giftLink(gift.url, gift.name) : gift.name), gift.value, 'ntn-qb-gift'));
    }
    // [GRATIS-GESCHENK-2] temporär deaktiviert (Curcuma ausverkauft):
    // if (hasSecondGift) {
    //   rows.push(checklistRow('Gratis Geschenk ' + (SECOND_GIFT.url ? giftLink(SECOND_GIFT.url, SECOND_GIFT.name) : SECOND_GIFT.name), SECOND_GIFT.value, 'ntn-qb-gift'));
    // }
    if (!rows.length) return null;

    var newHTML = rows.join('');
    if (hasDiscountBadge) {
      newHTML +=
        '<div class="ntn-qb-divider"></div>' +
        '<div class="ntn-qb-summary-line">= Extra Vorteile im Wert von ' +
        '<span class="ntn-qb-summary-amount">' + formatMoney(totalValue) + '</span></div>';
    }

    var block = label.querySelector('.' + INJECTED_CLASS);
    if (!block) {
      block = document.createElement('div');
      block.className = INJECTED_CLASS;
      label.appendChild(block);
    }
    if (block.getAttribute('data-content') !== newHTML) {
      block.setAttribute('data-content', newHTML);
      block.innerHTML = newHTML;
    }

    return { label: label, totalValue: totalValue };
  }

  function markBestValue(results) {
    if (results.length < 2) return;
    var best = results.reduce(function (a, b) {
      return b.totalValue > a.totalValue ? b : a;
    });
    results.forEach(function (r) {
      var existing = r.label.querySelector('.' + BEST_BADGE_CLASS);
      if (r === best) {
        if (!existing) {
          var badge = document.createElement('div');
          badge.className = BEST_BADGE_CLASS;
          badge.textContent = '⭐ BESTER WERT';
          r.label.insertBefore(badge, r.label.firstChild);
        }
      } else if (existing) {
        existing.remove();
      }
    });
  }

  function enhanceAll() {
    var gift = detectGift();
    var results = [];
    for (var i = 0; i < 20; i++) {
      if (!document.getElementById('pumper_totalAmount_' + i)) break;
      var r = enhanceTier(i, gift);
      if (r) results.push(r);
    }
    markBestValue(results);
  }

  function injectStyles() {
    if (document.getElementById('ntn-qb-style')) return;
    var style = document.createElement('style');
    style.id = 'ntn-qb-style';
    style.textContent = [
      '.' + INJECTED_CLASS + ' { width: 100%; margin-top: 8px; }',
      '.ntn-qb-row { display: flex; flex-wrap: wrap; align-items: flex-start; gap: 6px; font-size: 13px; font-weight: 500; color: #333; padding: 2px 0; }',
      '.ntn-qb-plus { color: #22ae22; font-weight: 700; font-size: 14px; flex-shrink: 0; }',
      '.ntn-qb-row-label { flex-shrink: 1; }',
      '.ntn-qb-gift { color: #b8791f; font-weight: 700; }',
      '.ntn-qb-gift .ntn-qb-plus { color: #b8791f; }',
      '.ntn-qb-gift-link { color: inherit; text-decoration: underline; text-underline-offset: 2px; }',
      '.ntn-qb-gift-link:hover { text-decoration-thickness: 2px; }',
      '.ntn-qb-value { margin-left: auto; padding-left: 6px; white-space: nowrap; text-align: right; color: #8a8a8a; font-weight: 400; }',
      '.ntn-qb-divider { border-top: 1px solid rgba(0,0,0,.08); margin: 10px 0 8px; }',
      '.ntn-qb-summary-line { font-size: 13px; font-weight: 400; color: #555; line-height: 1.4; }',
      '.ntn-qb-summary-amount { font-weight: 700; color: #1f6b1f; }',
      '.' + BEST_BADGE_CLASS + ' { display: inline-block; background: linear-gradient(90deg,#b8791f,#d9a03c); color: #fff; font-size: 11px; font-weight: 800; letter-spacing: .04em; text-transform: uppercase; padding: 4px 10px; border-radius: 999px; margin-bottom: 8px; }',
      '@media screen and (max-width: 749px) {',
      '  .ntn-qb-row { font-size: 12.5px; gap: 5px; }',
      '  .ntn-qb-summary-line { font-size: 12.5px; }',
      '}',
    ].join('\n');
    document.head.appendChild(style);
  }

  var pending = false;
  function schedule() {
    if (pending) return;
    pending = true;
    requestAnimationFrame(function () {
      pending = false;
      enhanceAll();
    });
  }

  function init() {
    injectStyles();
    enhanceAll();
    var observer = new MutationObserver(schedule);
    observer.observe(document.body, { childList: true, subtree: true, characterData: true });
  }

  if (document.body) {
    init();
  } else {
    document.addEventListener('DOMContentLoaded', init);
  }
})();
